import { useCallback, useEffect, useRef, useState } from 'react'

// Speech synthesis is missing in a few embedded browsers, so the caller hides
// its playback controls when `supported` is false.
const synthesis = () => {
  if (typeof window === 'undefined') return null
  if (typeof window.SpeechSynthesisUtterance !== 'function') return null
  return window.speechSynthesis || null
}

// Chrome silently cuts a single utterance off after roughly fifteen seconds, so
// a reply is spoken as a queue of short chunks rather than one long one.
const MAX_BROWSER_CHUNK_CHARS = 180

// Generating audio costs about a second per few hundred characters, and nothing
// can play until the clip it belongs to is finished. A short opening clip is
// what makes Stella start talking promptly; later clips are generated while an
// earlier one plays, so they can be long enough to keep her phrasing natural.
const FIRST_GENERATED_CHUNK_CHARS = 140
const MAX_GENERATED_CHUNK_CHARS = 900

// Cancelling and speaking in the same tick drops the new utterance in Chrome.
const RESTART_DELAY_MS = 60

// Voices the platform ships that sound closest to a person rather than a
// screen reader. Anything unlisted still plays with the platform default.
const PREFERRED_VOICES = [
  'samantha',
  'google us english',
  'microsoft aria',
  'microsoft jenny',
  'zira',
]

const TABLE_DELIMITER = /^\|?[\s:|-]+$/
const HORIZONTAL_RULE = /^([-*_])\s*\1\s*\1[-*_\s]*$/

// Splits on word boundaries so a sentence with no punctuation still yields
// chunks the browser will finish speaking.
const splitLongPiece = (piece, maxChars) => {
  const parts = []
  let current = ''
  piece.split(' ').forEach((word) => {
    if (current && `${current} ${word}`.length > maxChars) {
      parts.push(current)
      current = word
    } else {
      current = current ? `${current} ${word}` : word
    }
  })
  if (current) parts.push(current)
  return parts
}

const chunkText = (text, limitAt) => {
  const pieces = String(text).match(/[^.!?]+[.!?]*\s*/g) || []
  const chunks = []
  let current = ''

  const flush = () => {
    const trimmed = current.trim()
    if (trimmed) chunks.push(trimmed)
    current = ''
  }

  pieces.forEach((piece) => {
    const sentence = piece.replace(/\s+/g, ' ')
    if (!sentence.trim()) return
    const maxChars = limitAt(chunks.length)
    if (sentence.length > maxChars) {
      flush()
      splitLongPiece(sentence.trim(), limitAt(chunks.length)).forEach((part) =>
        chunks.push(part)
      )
      return
    }
    if ((current + sentence).length > maxChars) flush()
    current += sentence
  })

  flush()
  return chunks
}

const chunkForSpeech = (text) => chunkText(text, () => MAX_BROWSER_CHUNK_CHARS)

const chunkForGeneratedSpeech = (text) =>
  chunkText(text, (index) =>
    index === 0 ? FIRST_GENERATED_CHUNK_CHARS : MAX_GENERATED_CHUNK_CHARS
  )

const pickVoice = (voices, language) => {
  if (!voices.length) return null
  const locale = String(language || 'en-US')
    .toLowerCase()
    .replace('_', '-')
  const sameLocale = voices.filter(
    (voice) =>
      String(voice.lang || '')
        .toLowerCase()
        .replace('_', '-') === locale
  )
  const sameLanguage = voices.filter((voice) =>
    String(voice.lang || '')
      .toLowerCase()
      .startsWith(locale.slice(0, 2))
  )
  const pool = sameLocale.length
    ? sameLocale
    : sameLanguage.length
      ? sameLanguage
      : voices
  const preferred = pool.find((voice) =>
    PREFERRED_VOICES.some((name) =>
      String(voice.name || '')
        .toLowerCase()
        .includes(name)
    )
  )
  return preferred || pool[0]
}

// Stella answers in markdown, and the markup itself is not worth listening to:
// asterisks, links, and pipe tables all read as noise. Reduce a reply to the
// sentences it is actually made of.
const toSpeechText = (markdown) => {
  const withoutBlocks = String(markdown || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')

  const lines = []
  withoutBlocks.split('\n').forEach((raw) => {
    const line = raw.trim()
    if (!line) return
    if (HORIZONTAL_RULE.test(line)) return
    if (
      line.includes('|') &&
      line.includes('-') &&
      TABLE_DELIMITER.test(line)
    ) {
      return
    }

    const cells = line.startsWith('|')
      ? line
          .replace(/^\||\|$/g, '')
          .split('|')
          .map((cell) => cell.trim())
          .filter(Boolean)
          .join(', ')
      : line

    const plain = cells
      .replace(/^#{1,6}\s*/, '')
      .replace(/^>\s?/, '')
      .replace(/^[-*+]\s+/, '')
      .replace(/^\d+[.)]\s+/, '')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/__(.+?)__/g, '$1')
      .replace(/~~(.+?)~~/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .trim()

    if (plain) lines.push(plain)
  })

  // Headings and list items carry no terminal punctuation, so the browser would
  // otherwise run them into the next line without a pause.
  return lines
    .map((line) => (/[.!?:;,]$/.test(line) ? line : `${line}.`))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// Reads Stella's replies aloud. Playback is tracked by an id so a message can
// show whether it is the one currently being spoken.
export default function useSpeechPlayback({ generateSpeech } = {}) {
  const synth = synthesis()
  const generatedAudioSupported =
    typeof generateSpeech === 'function' &&
    typeof window !== 'undefined' &&
    typeof window.Audio === 'function' &&
    typeof URL?.createObjectURL === 'function'
  const supported = generatedAudioSupported || Boolean(synth)

  const [speaking, setSpeaking] = useState(false)
  // True between asking for the opening clip and hearing it, so the panel can
  // say Stella is getting ready rather than claiming she is already talking.
  const [preparing, setPreparing] = useState(false)
  const [activeId, setActiveId] = useState(null)
  const [error, setError] = useState('')

  const voicesRef = useRef([])
  // Bumped on every stop and every new reply, so handlers left over from a
  // cancelled queue cannot advance or clear the queue that replaced it.
  const sessionRef = useRef(0)
  const timerRef = useRef(null)
  const audioRef = useRef(null)
  const objectUrlRef = useRef(null)

  const clearGeneratedAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.removeAttribute?.('src')
      audioRef.current = null
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL?.(objectUrlRef.current)
      objectUrlRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!synth) return undefined
    const load = () => {
      voicesRef.current = synth.getVoices() || []
    }
    load()
    // Chrome resolves its voice list asynchronously on first use.
    synth.addEventListener?.('voiceschanged', load)
    return () => synth.removeEventListener?.('voiceschanged', load)
  }, [synth])

  const stop = useCallback(() => {
    sessionRef.current += 1
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setSpeaking(false)
    setPreparing(false)
    setActiveId(null)
    clearGeneratedAudio()
    try {
      synth?.cancel()
    } catch (_error) {
      // Cancelling when nothing is queued is not a failure.
    }
  }, [clearGeneratedAudio, synth])

  const speak = useCallback(
    (text, id = null) => {
      if (!supported) return
      const chunks = chunkForSpeech(toSpeechText(text))
      if (!chunks.length) return

      sessionRef.current += 1
      const session = sessionRef.current
      if (timerRef.current) clearTimeout(timerRef.current)
      try {
        synth.cancel()
      } catch (_error) {
        // Nothing was playing.
      }

      setError('')
      setSpeaking(true)
      setPreparing(generatedAudioSupported)
      setActiveId(id)

      const voice = pickVoice(
        voicesRef.current,
        typeof navigator !== 'undefined' ? navigator.language : 'en-US'
      )

      const speakBrowserChunks = (index = 0) => {
        if (!synth || session !== sessionRef.current) return
        if (index >= chunks.length) {
          setSpeaking(false)
          setActiveId(null)
          return
        }

        const utterance = new window.SpeechSynthesisUtterance(chunks[index])
        if (voice) {
          utterance.voice = voice
          utterance.lang = voice.lang
        }
        utterance.rate = 1.02
        utterance.onend = () => speakBrowserChunks(index + 1)
        utterance.onerror = (event) => {
          if (session !== sessionRef.current) return
          // Stopping playback surfaces here too, and that is not a fault.
          if (event?.error === 'interrupted' || event?.error === 'canceled') {
            return
          }
          setSpeaking(false)
          setActiveId(null)
          setError('Playback stopped unexpectedly. Try again.')
        }
        synth.speak(utterance)
      }

      if (generatedAudioSupported) {
        const generatedChunks = chunkForGeneratedSpeech(toSpeechText(text))

        // Generating a clip is the slow part, so the next one is requested
        // before the current one starts playing rather than after it ends.
        const requests = new Map()
        const request = (index) => {
          if (index >= generatedChunks.length) return null
          if (!requests.has(index)) {
            const pending = generateSpeech(generatedChunks[index])
            // A prefetched clip is abandoned whenever playback is stopped, and
            // an abandoned rejection must not surface as an unhandled one.
            pending.catch(() => {})
            requests.set(index, pending)
          }
          return requests.get(index)
        }

        const playGeneratedChunk = async (index) => {
          if (session !== sessionRef.current) return
          if (index >= generatedChunks.length) {
            clearGeneratedAudio()
            setSpeaking(false)
            setPreparing(false)
            setActiveId(null)
            return
          }

          try {
            const pending = request(index)
            request(index + 1)
            const blob = await pending
            if (session !== sessionRef.current) return
            clearGeneratedAudio()
            setPreparing(false)
            const objectUrl = URL.createObjectURL(blob)
            const audio = new window.Audio(objectUrl)
            objectUrlRef.current = objectUrl
            audioRef.current = audio
            audio.onended = () => playGeneratedChunk(index + 1)
            audio.onerror = () => {
              if (session !== sessionRef.current) return
              clearGeneratedAudio()
              setSpeaking(false)
              setPreparing(false)
              setActiveId(null)
              setError('Stella could not play the generated voice.')
            }
            await audio.play()
          } catch (_error) {
            if (session !== sessionRef.current) return
            // Network and provider failures should not take voice playback away
            // entirely; the platform voice remains a useful fallback.
            clearGeneratedAudio()
            setPreparing(false)
            if (synth) {
              speakBrowserChunks()
              return
            }
            setSpeaking(false)
            setActiveId(null)
            setError('Stella could not generate speech. Try again.')
          }
        }

        playGeneratedChunk(0)
        return
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = null
        speakBrowserChunks()
      }, RESTART_DELAY_MS)
    },
    [
      clearGeneratedAudio,
      generateSpeech,
      generatedAudioSupported,
      supported,
      synth,
    ]
  )

  useEffect(
    () => () => {
      sessionRef.current += 1
      if (timerRef.current) clearTimeout(timerRef.current)
      clearGeneratedAudio()
      try {
        synthesis()?.cancel()
      } catch (_error) {
        // Nothing to clean up when playback never started.
      }
    },
    [clearGeneratedAudio]
  )

  return { supported, speaking, preparing, activeId, error, speak, stop }
}

export { toSpeechText }
